import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Combatant, Countdown, FullState, ServerMessage } from '@sd/shared';
import { AppHeader } from '../components/AppHeader.js';
import { useGm } from '../hooks/useGm.js';
import { getGmSecret } from '../lib/gm.js';
import { EncounterConnection } from '../lib/ws.js';
import { TacticalMap } from '../components/combat/TacticalMap.js';
import { CombatantDetail } from '../components/combat/CombatantDetail.js';
import { GmQuickMenu } from '../components/combat/QuickMenu.js';
import { SourcePicker } from '../components/combat/SourcePicker.js';
import { useFxQueue } from '../hooks/useFxQueue.js';
import { useTheme, themeClass } from '../hooks/useTheme.js';
import { DiceRoller, type DiceRollResult } from '../components/DiceRoller.js';
import { D20Roller, type D20RollResult } from '../components/D20Roller.js';
import '../styles/encounter.css';
import '../styles/combat.css';

export function EncounterGm(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { isGm } = useGm();
  const [state, setState] = useState<FullState | null>(null);
  const [conn, setConn] = useState<'connecting' | 'open' | 'closed' | 'error'>('connecting');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const wsRef = useRef<EncounterConnection | null>(null);
  const fx = useFxQueue();
  const { theme } = useTheme();
  /** Dice-config для GM-роллів. Union: duality (грати за гравця/гравця-аналога)
   *  або d20 (GM-атака на влучання). */
  type DiceConfig =
    | { kind: 'duality'; modifier: number; label?: string; autoRoll: boolean;
        playback?: { hope: number; fear: number; advDie?: number; modifier?: number } }
    | { kind: 'd20'; modifier: number; label?: string; advantage?: 'none' | 'advantage' | 'disadvantage';
        autoRoll: boolean;
        playback?: { dice: number[]; pickedIndex: number; modifier?: number } };
  const [diceConfig, setDiceConfig] = useState<DiceConfig | null>(null);
  /** Last log subscription — щоб GM теж бачив playback dice */
  const lastDiceLogAtRef = useRef<number>(0);
  useEffect(() => {
    if (!state) return;
    const log = state.encounter.log;
    if (!log.length) return;
    for (const e of log.filter((x) => x.at > lastDiceLogAtRef.current)) {
      if (e.kind !== 'roll' || !e.roll || diceConfig?.autoRoll) continue;
      const r = e.roll;
      const label = r.label ?? (e.who ?? 'Roll');
      if (r.kind === 'd20' && r.dice && r.pickedIndex !== undefined) {
        setDiceConfig({
          kind: 'd20',
          modifier: r.modifier ?? 0,
          label,
          autoRoll: false,
          playback: { dice: r.dice, pickedIndex: r.pickedIndex, modifier: r.modifier },
        });
      } else if (r.hope !== undefined && r.fear !== undefined) {
        setDiceConfig({
          kind: 'duality',
          modifier: r.modifier ?? 0,
          label,
          autoRoll: false,
          playback: { hope: r.hope, fear: r.fear, advDie: r.advDie, modifier: r.modifier },
        });
      }
    }
    const maxAt = log[log.length - 1]?.at ?? 0;
    if (maxAt > lastDiceLogAtRef.current) lastDiceLogAtRef.current = maxAt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const fxPush = fx.push; // стабільний ref з useCallback — тримаємо лише його у deps
  /** Трекаємо max log.at, не id: коли сервер обрізає log (>500), id зникає */
  const lastLogAtRef = useRef<number>(0);

  // Підписка на нові log-події з fx → пушимо в FX queue
  useEffect(() => {
    if (!state) return;
    const log = state.encounter.log;
    if (!log.length) return;
    const newEntries = log.filter((e) => e.at > lastLogAtRef.current);
    for (const e of newEntries) {
      if (e.fx && e.targetId) {
        fxPush(e.fx, e.targetId, { value: e.value, fromId: e.fromId, projKind: e.projKind });
      }
    }
    const maxAt = log[log.length - 1]?.at ?? 0;
    if (maxAt > lastLogAtRef.current) lastLogAtRef.current = maxAt;
  }, [state, fxPush]);

  useEffect(() => {
    if (!isGm) return;
    const secret = getGmSecret();
    if (!secret) return;
    const c = new EncounterConnection({
      encounterId: id,
      role: 'gm',
      credential: secret,
      onMessage: (m: ServerMessage) => {
        if (m.type === 'state:full' || m.type === 'state:patch') {
          setState(m.payload as FullState);
        }
      },
      onStatus: setConn,
    });
    wsRef.current = c;
    return () => c.close();
  }, [id, isGm]);

  if (!isGm) {
    return (
      <div className="encounter-shell combat-fit">
        <AppHeader />
        <div className="encounter-body">
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)' }}>GM only</h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Ця сторінка доступна лише з GM-секретом. Відкрий <code>/gm/&lt;твій-секрет&gt;</code>.
          </p>
        </div>
      </div>
    );
  }

  const playerUrl = `${window.location.origin}/room/${encodeURIComponent(id)}/join`;
  const ws = wsRef.current;
  const send = (msg: Parameters<NonNullable<typeof ws>['send']>[0]): void => { ws?.send(msg); };

  const selected = selectedId ? state?.combatants.find((c) => c.id === selectedId) : null;

  const setStat = (c: Combatant, field: 'hp' | 'stress' | 'armor', value: number): void => {
    const newStats = { ...c.stats };
    const max = field === 'hp' ? c.stats.hpMax : field === 'stress' ? c.stats.stressMax : c.stats.armorMax;
    newStats[field] = Math.max(0, Math.min(max, value));
    send({ type: 'combatant:upsert', payload: { id: c.id, stats: newStats } });
  };

  const toggleCondition = (c: Combatant, type: string, on: boolean): void => {
    send({ type: 'combatant:condition', payload: { id: c.id, condition: type as Combatant['conditions'][number], on } });
  };

  const onTokenMove = (combatantId: string, x: number, y: number): void => {
    send({ type: 'token:move', payload: { id: combatantId, x, y } });
  };

  const deleteCombatant = (combatantId: string): void => {
    if (!window.confirm('Видалити цього combatant-а з зустрічі?')) return;
    send({ type: 'combatant:delete', payload: { id: combatantId } });
    setSelectedId(null);
  };

  const exportJson = async (): Promise<void> => {
    const secret = getGmSecret();
    const res = await fetch(`/api/encounters/${encodeURIComponent(id)}/export`, {
      headers: { 'X-GM-Secret': secret ?? '' },
    });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `encounter-${id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="encounter-shell combat-fit">
      <AppHeader />

      <div className={`combat-page ${themeClass(theme)}`.trim()}>
        {/* Top toolbar: Fear + Roster */}
        <div className="combat-toolbar">
          <FearPill
            value={state?.encounter.fear ?? 0}
            onAdjust={(d) => send({ type: 'fear:adjust', payload: { delta: d } })}
            onSet={(v) => send({ type: 'fear:set', payload: { value: v } })}
          />
          <div className="roster">
            {state?.combatants.map((c) => (
              <RosterChip
                key={c.id}
                combatant={c}
                selected={selectedId === c.id}
                onClick={() => setSelectedId(c.id)}
              />
            ))}
            <button
              onClick={() => setShowAddForm((v) => !v)}
              style={{
                background: 'var(--bg-card)', border: '1px dashed var(--gold-dim)',
                color: 'var(--gold)', padding: '8px 14px', borderRadius: 4,
                cursor: 'pointer', fontFamily: 'Cormorant Garamond, serif', fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >+ Додати</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className={`conn-pill ${conn}`}>{conn}</span>
            <Link to={`/encounter/${encodeURIComponent(id)}/track`} style={{ color: 'var(--gold)', fontSize: 11 }}>Трекер →</Link>
            <Link to="/encounters" style={{ color: 'var(--text-dim)', fontSize: 11 }}>← усі</Link>
            <button
              onClick={() => void exportJson()}
              style={{
                background: 'none', border: '1px solid var(--border)',
                color: 'var(--text-muted)', fontSize: 10, padding: '3px 8px',
                borderRadius: 2, cursor: 'pointer', fontFamily: 'Spline Sans Mono, monospace',
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}
            >⤓ JSON</button>
          </div>
        </div>

        {/* Counters */}
        <CountersBar
          counters={state?.encounter.countdowns ?? []}
          onUpsert={(c) => send({ type: 'counter:upsert', payload: c })}
          onDelete={(cid) => send({ type: 'counter:delete', payload: { id: cid } })}
        />

        {/* Main: map + selected card */}
        <div className="combat-main">
          <div className="pane-map">
            {state ? (
              <TacticalMap
                map={state.encounter.map}
                combatants={state.combatants}
                canMove={() => true}
                onMove={onTokenMove}
                selectedId={selectedId}
                onSelect={setSelectedId}
                fxs={fx.fxs}
                targetId={targetId}
                // GM: лівий клік → quick-menu під токеном; ПКМ → вибір для detail-картки.
                onSetTarget={setTargetId}
                renderQuickMenu={(t, close) => (
                  <GmQuickMenu
                    token={t}
                    close={close}
                    isTarget={targetId === t.id}
                    onTarget={() => setTargetId(targetId === t.id ? null : t.id)}
                    onInfo={() => setSelectedId(t.id)}
                    onConditionToggle={(cond, on) => send({ type: 'combatant:condition', payload: { id: t.id, condition: cond, on } })}
                    onDelete={() => {
                      if (window.confirm(`Видалити «${t.name}»?`)) {
                        send({ type: 'combatant:delete', payload: { id: t.id } });
                        if (selectedId === t.id) setSelectedId(null);
                      }
                    }}
                  />
                )}
              />
            ) : <div style={{ padding: 24, color: 'var(--text-dim)' }}>завантажую…</div>}
          </div>
          <div className="pane-side">
            <div className="copy-url" style={{ marginBottom: 12 }}>
              <span style={{ color: 'var(--gold)', fontSize: 11 }}>Гравцям:</span>
              <input readOnly value={playerUrl} onClick={(e) => (e.target as HTMLInputElement).select()} />
              <button onClick={() => navigator.clipboard?.writeText(playerUrl)}>Копія</button>
            </div>

            {selected ? (
              <>
                {/* Target banner для GM */}
                <div style={{
                  display: 'flex', gap: 6, alignItems: 'center',
                  padding: '5px 8px', marginBottom: 8,
                  background: targetId ? 'rgba(157,79,74,0.15)' : 'var(--bg-deep)',
                  border: `1px dashed ${targetId ? 'var(--blood-bright)' : 'var(--border)'}`,
                  borderRadius: 3,
                  fontFamily: 'Spline Sans Mono, monospace', fontSize: 10,
                }}>
                  <span style={{ color: 'var(--text-dim)' }}>Ціль:</span>
                  <span style={{ color: targetId ? 'var(--blood-bright)' : 'var(--text-dim)', flex: 1, fontFamily: 'Cormorant Garamond, serif' }}>
                    {targetId ? state?.combatants.find((c) => c.id === targetId)?.name ?? '???' : '— Shift+клік на мапі —'}
                  </span>
                  {targetId && (
                    <button
                      onClick={() => setTargetId(null)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 14, padding: 0 }}
                      title="Скасувати ціль"
                    >×</button>
                  )}
                </div>
                <CombatantDetail
                  combatant={selected}
                  onHpSet={(v) => setStat(selected, 'hp', v)}
                  onStressSet={(v) => setStat(selected, 'stress', v)}
                  onArmorSet={(v) => setStat(selected, 'armor', v)}
                  onConditionToggle={(type, on) => toggleCondition(selected, type, on)}
                  onDelete={() => deleteCombatant(selected.id)}
                  // Майстер на влучання кидає d20 (а не 2d12 duality — то для гравців).
                  onAttackRoll={(modifier, label) => setDiceConfig({ kind: 'd20', modifier, label, autoRoll: true, advantage: 'none' })}
                  onDamageRoll={(formula, label, damageType) => send({
                    type: 'damage:roll',
                    payload: { formula, label, damageType, fromId: selected.id, toId: targetId ?? undefined },
                  })}
                  onAppearanceChange={(patch) => send({ type: 'combatant:upsert', payload: { id: selected.id, ...patch } })}
                />
              </>
            ) : (
              <p style={{ color: 'var(--text-dim)', fontStyle: 'italic', textAlign: 'center', padding: 24 }}>
                Клікни на токен або roster-чип, щоб побачити деталі
              </p>
            )}
          </div>
        </div>

        {/* Log */}
        <LogPanel
          log={state?.encounter.log ?? []}
          onWrite={(text) => send({ type: 'log:write', payload: { text, kind: 'narration', who: 'GM' } })}
        />
      </div>

      {/* Add combatant modal — tabs (з джерел / вручну) */}
      {showAddForm && (
        <AddCombatantModal
          onSubmit={(payload) => {
            send({ type: 'combatant:upsert', payload: { ...payload, encounterId: id } });
            setShowAddForm(false);
          }}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* GM Dice modal — for attack rolls of adversaries/allies + playback */}
      {diceConfig && (
        <div
          onClick={() => setDiceConfig(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 6, padding: 24, minWidth: 480, maxWidth: 560,
              boxShadow: 'var(--shadow-deep)',
            }}
          >
            {diceConfig.kind === 'd20' ? (
              <D20Roller
                label={diceConfig.label}
                initialModifier={diceConfig.modifier}
                initialAdvantage={diceConfig.advantage}
                autoRoll={diceConfig.autoRoll}
                playback={diceConfig.playback}
                onClose={() => setDiceConfig(null)}
                onRollComplete={(result: D20RollResult) => {
                  // Не пушимо playback (бо вже з лога) — лише свіжі ролли.
                  if (diceConfig.autoRoll && !diceConfig.playback) {
                    const projKind: 'arrow' | 'spell' | 'melee' =
                      /лук|стріл|кидальн/i.test(diceConfig.label ?? '') ? 'arrow'
                      : /магі|спел|жезл/i.test(diceConfig.label ?? '') ? 'spell'
                      : 'melee';
                    const advantage = result.dice.length === 2
                      ? (result.dice[result.pickedIndex] === Math.max(...result.dice) ? 'advantage' : 'disadvantage')
                      : 'none';
                    send({
                      type: 'dice:roll',
                      payload: {
                        kind: 'd20',
                        modifier: result.modifier,
                        advantage,
                        result: { dice: result.dice, pickedIndex: result.pickedIndex },
                        label: diceConfig.label,
                        fromId: selected?.id,
                        toId: targetId ?? undefined,
                        projKind,
                      },
                    });
                  }
                }}
              />
            ) : (
              <DiceRoller
                label={diceConfig.label}
                initialModifier={diceConfig.modifier}
                autoRoll={diceConfig.autoRoll}
                playback={diceConfig.playback}
                onClose={() => setDiceConfig(null)}
                onRollComplete={(result: DiceRollResult) => {
                  if (diceConfig.autoRoll && !diceConfig.playback) {
                    send({
                      type: 'dice:roll',
                      payload: {
                        kind: 'duality',
                        modifier: result.modifier,
                        advantage: result.advDie ? (result.advDie > 0 ? 'advantage' : 'disadvantage') : 'none',
                        result: { hope: result.hope, fear: result.fear, advDie: result.advDie },
                        label: diceConfig.label,
                        fromId: selected?.id,
                        toId: targetId ?? undefined,
                      },
                    });
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Fear pill ─────────────────────────────────────────────────────────────
function FearPill({ value, onAdjust, onSet }: {
  value: number;
  onAdjust: (delta: number) => void;
  onSet: (v: number) => void;
}): JSX.Element {
  void onSet;
  const segs = Array.from({ length: 12 }, (_, i) => i < value);
  return (
    <div className="fear-pill">
      <div className="fp-top">Страх</div>
      <div className="fp-bar">{segs.map((f, i) => <div key={i} className={`fp-seg ${f ? 'filled' : ''}`} />)}</div>
      <div className="fp-controls">
        <button onClick={() => onAdjust(-1)} disabled={value <= 0}>−</button>
        <span className="fp-value">{value}/12</span>
        <button onClick={() => onAdjust(+1)} disabled={value >= 12}>+</button>
      </div>
    </div>
  );
}

// ── Roster chip ───────────────────────────────────────────────────────────
function RosterChip({ combatant: c, selected, onClick }: {
  combatant: Combatant;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const initial = (c.name?.[0] ?? '?').toUpperCase();
  const hpRatio = c.stats.hpMax > 0 ? c.stats.hp / c.stats.hpMax : 1;
  const hpClass = hpRatio <= 1 / 3 ? 'critical' : hpRatio <= 2 / 3 ? 'hurt' : '';
  return (
    <div className={`roster-chip ${c.kind} ${selected ? 'selected' : ''}`} onClick={onClick}>
      <div className="rc-swatch" style={{ background: c.tokenColor ?? 'var(--bg-deep)' }}>
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', color: 'var(--bg-deep)', fontWeight: 600 }}>
          {initial}
        </div>
      </div>
      <div className="rc-body">
        <div className="rc-name">{c.name}</div>
        <div className={`rc-hp ${hpClass}`}>{c.stats.hp}/{c.stats.hpMax} HP · {c.stats.stress}/{c.stats.stressMax} Str</div>
      </div>
    </div>
  );
}

// ── Counters ──────────────────────────────────────────────────────────────
function CountersBar({ counters, onUpsert, onDelete }: {
  counters: Countdown[];
  onUpsert: (c: Countdown) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newMax, setNewMax] = useState(6);

  const addNew = (): void => {
    if (!newLabel.trim()) return;
    onUpsert({
      id: 'ctr-' + Date.now().toString(36),
      label: newLabel.trim(),
      kind: 'progress',
      current: 0,
      max: newMax,
    });
    setNewLabel(''); setNewMax(6); setAdding(false);
  };

  return (
    <div className="counters-bar">
      {counters.map((c) => (
        <div key={c.id} className="counter">
          <span className="ctr-label">{c.label}</span>
          <div className="ctr-pips">
            {Array.from({ length: c.max }, (_, i) => (
              <div key={i} className={`ctr-pip ${i < c.current ? 'on' : ''}`} />
            ))}
          </div>
          <button onClick={() => onUpsert({ ...c, current: Math.max(0, c.current - 1) })} title="−1">−</button>
          <button onClick={() => onUpsert({ ...c, current: Math.min(c.max, c.current + 1) })} title="+1">+</button>
          <button onClick={() => onDelete(c.id)} title="видалити" style={{ color: 'var(--blood-bright)' }}>×</button>
        </div>
      ))}
      {adding ? (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="назва"
            autoFocus
            style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 8px', borderRadius: 3, fontSize: 12, width: 140 }}
          />
          <input
            type="number" min={1} max={12}
            value={newMax}
            onChange={(e) => setNewMax(Number(e.target.value) || 6)}
            style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 8px', borderRadius: 3, fontSize: 12, width: 50 }}
          />
          <button className="add-counter" onClick={addNew}>OK</button>
          <button className="add-counter" onClick={() => setAdding(false)}>×</button>
        </div>
      ) : (
        <button className="add-counter" onClick={() => setAdding(true)}>+ лічильник</button>
      )}
    </div>
  );
}

// ── Add combatant form ────────────────────────────────────────────────────
function AddCombatantForm({ onSubmit, onCancel }: {
  onSubmit: (payload: Partial<Combatant>) => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'adversary' | 'player' | 'ally'>('player');
  const [hp, setHp] = useState(6);
  const [stress, setStress] = useState(6);
  const [armor, setArmor] = useState(3);
  const [color, setColor] = useState('#cca766');

  const submit = (): void => {
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      kind,
      // Daggerheart-семантика marks: 0 = здоровий. Тому hp/stress/armor стартують з 0.
      stats: {
        hpMax: hp, hp: 0,
        stressMax: stress, stress: 0,
        armorMax: armor, armor: 0,
        attacks: [], features: [],
      },
      conditions: [],
      tokenX: 5, tokenY: 5,
      tokenColor: color,
      hidden: false,
      isMinionGroup: false,
      count: 1,
    });
  };

  return (
    <div className="add-combatant-form">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ім'я" autoFocus />
      <div className="row">
        <select value={kind} onChange={(e) => setKind(e.target.value as 'adversary' | 'player' | 'ally')}>
          <option value="player">Гравець</option>
          <option value="ally">Союзник</option>
          <option value="adversary">Противник</option>
        </select>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
      </div>
      <div className="row">
        <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>HP <input type="number" min={1} max={20} value={hp} onChange={(e) => setHp(Number(e.target.value))} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>Stress <input type="number" min={0} max={12} value={stress} onChange={(e) => setStress(Number(e.target.value))} /></label>
      </div>
      <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>Броня <input type="number" min={0} max={10} value={armor} onChange={(e) => setArmor(Number(e.target.value))} /></label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={submit}>Додати</button>
        <button onClick={onCancel} style={{ background: 'var(--bg-deep)', color: 'var(--text-muted)' }}>Скасувати</button>
      </div>
    </div>
  );
}

// ── Add combatant modal: tabs (Sources / Manual) ──────────────────────────
function AddCombatantModal({ onSubmit, onCancel }: {
  onSubmit: (payload: Partial<Combatant>) => void;
  onCancel: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<'sources' | 'manual'>('sources');
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card add-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-tabs">
          <button
            type="button"
            className={`modal-tab ${tab === 'sources' ? 'active' : ''}`}
            onClick={() => setTab('sources')}
          >З джерел</button>
          <button
            type="button"
            className={`modal-tab ${tab === 'manual' ? 'active' : ''}`}
            onClick={() => setTab('manual')}
          >Вручну</button>
          <div className="modal-tabs-spacer" />
          <button type="button" className="modal-close" onClick={onCancel} title="Закрити (Esc)">×</button>
        </div>
        <div className="modal-body">
          {tab === 'sources' && <SourcePicker onAdd={onSubmit} onClose={onCancel} />}
          {tab === 'manual' && <AddCombatantForm onSubmit={onSubmit} onCancel={onCancel} />}
        </div>
      </div>
    </div>
  );
}

// ── Log panel ─────────────────────────────────────────────────────────────
function LogPanel({ log, onWrite }: {
  log: FullState['encounter']['log'];
  onWrite: (text: string) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [log.length]);

  return (
    <div className="log-panel" ref={scrollRef}>
      {log.length === 0 && <div style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>Журнал порожній</div>}
      {log.map((e) => (
        <div key={e.id} className={`entry ${e.kind ?? ''}`}>
          <span className="time">{new Date(e.at * 1000).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          {e.who && <span style={{ color: 'var(--gold-dim)' }}>{e.who}: </span>}
          {e.text}
        </div>
      ))}
      <div className="form">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) { onWrite(text); setText(''); } }}
          placeholder="Наративний запис (Enter — додати)"
        />
        <button onClick={() => { if (text.trim()) { onWrite(text); setText(''); } }}>+</button>
      </div>
    </div>
  );
}
